import { useEffect, useState } from "react";
import {
    ListContainer,
    ListContext,
    PAGE_DEFAULT,
    PAGE_SIZE_UNLIMITED,
    Page,
    PageRoute,
    SearchBar,
    buildTemplatePath,
} from "@compass/core";
import { ListFooter } from "components";
import { ListFilterBase, Permission, SimpleDto, LEPListDto, LEPListResource } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { LEPListPageHeader } from "./LEPListPageHeader";
import { LEPTable } from "./LEPTable";

const routeName = "lep";

export const lepListPageRoute: PageRoute = {
    handle: "LEP",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildTemplatePath(routeName),
};

export const LEPListPage = () => {
    const ROOT_PERMISSION = Permission.MatchUtility;
    const [listItems, setListItems] = useState<LEPListDto[]>([]);

    const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
        sortObjects: [{ property: "name", isDesc: false }],
    });

    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<LEPListResource | null>(null);
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter,
        setListFilter,
        fetchList: async (listFilter) => {
            const data = await ApiService.lep.getAll();
            setListItems(data || []);
        },
        fetchTotal: async (listFilter) => {
            const data = await ApiService.lep.getAll();
            setTotal(data?.length || 0);
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.lep.getListResource()
            .then(setResource)
            .catch((err) => {
                console.error("Failed to fetch list resource", err);
            });
    }, []);

    return (
        <Page header={<LEPListPageHeader rootPermission={ROOT_PERMISSION} handle={lepListPageRoute.handle} />}>
            <ListContainer
                fullHeight
                header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
                tableContainer={
                    resource && (
                        <LEPTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            leps={listItems}
                            setLeps={setListItems}
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                        />
                    )
                }
                footer={
                    <ListFooter
                        total={total}
                        size={listFilter.pageSize}
                        page={listFilter.page}
                        onPageChange={applyPage}
                        onSizeChange={applyPageSize}
                        onSortMethodChange={applySortMethod}
                    />
                }
            />
            <ListContext.Provider
                value={{
                    loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
                }}
            >
                <Outlet />
            </ListContext.Provider>
        </Page>
    );
};